import _merge from "lodash/merge";
import Box from '@material-ui/core/Box';
import InputAdornment from '@material-ui/core/InputAdornment';
import { alpha, makeStyles } from '@material-ui/core/styles';
import MuiTextField from '@material-ui/core/TextField';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import clsx from 'clsx';
import { memo, useMemo, useState } from 'react';
import InputHelperText from '../Input/InputHelperText';
import palette from '../palette';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useStyles = makeStyles(theme => ({
  disabled: {
    backgroundColor: palette.STONE,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: palette.STONE
    },
    borderRadius: 'inherit',
    '& input': {
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
        WebkitBoxShadow: '0 0 0 40px #F0F0F0 inset',
        backgroundColor: palette.STONE,
        backgroundClip: 'content-box'
      }
    }
  },
  disabledWithHelperText: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
  },
  container: {
    borderRadius: 'inherit',
    display: 'flex',
    flexDirection: 'column'
  },
  inputRoot: {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent'
    },
    '&$inputFocused': {
      backgroundColor: 'transparent'
    },
    '&$inputDisabled': {
      backgroundColor: 'transparent'
    }
  },
  adornedEnd: {
    '& svg': {
      color: palette.ROCK
    }
  },
  adornedEndFocused: {
    '& svg': {
      color: palette.INDIGO
    }
  },
  adornedEndError: {
    '& svg': {
      color: palette.HOTEL_CALIFORNIA
    }
  },
  inputOutlined: {
    borderRadius: 8,
    position: 'relative',
    border: `1px solid ${palette.ROCK}`,
    transition: theme.transitions.create('box-shadow', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    margin: -1,
    background: theme.palette.common.white
  },
  inputInline: {
    display: 'inline-block'
  },
  inputOutlinedFocused: {
    boxShadow: `${alpha(palette.INDIGO, 0.25)} 0 0 0 3px`,
    borderColor: palette.INDIGO,
    zIndex: 3
  },
  inputOutlinedValid: {
    borderColor: palette.GREEN_DAY,
    zIndex: 1
  },
  inputOutlinedValidFocused: {
    borderColor: palette.GREEN_DAY,
    boxShadow: `${alpha(palette.GREEN_DAY, 0.25)} 0 0 0 3px`,
    zIndex: 3
  },
  inputOutlinedError: {
    backgroundColor: palette.BLUSH,
    borderColor: palette.HOTEL_CALIFORNIA,
    zIndex: 2
  },
  inputOutlinedErrorFocused: {
    boxShadow: `${palette.BLUSH} 0 0 0 3px`,
    zIndex: 3
  },
  inputOutlinedDisabled: {
    boxShadow: 'none',
    backgroundColor: palette.STONE,
    borderColor: alpha(theme.palette.common.black, 0.38)
  },
  inputFocused: {},
  inputDisabled: {},
  inputInput: {
    padding: theme.spacing(4, 2, 1.5),
    transition: 'padding 0.1s',
    willChange: 'padding',
    '&[type="date"], &[type="time"]': {
      '-webkit-appearance': 'none'
    },
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
      borderRadius: 8,
      WebkitBoxShadow: '0 0 0 40px #fff inset',
      backgroundClip: 'content-box'
    },
    '&::-webkit-date-and-time-value': {
      textAlign: 'left'
    }
  },
  inputInputOutlinedError: {
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
      WebkitBoxShadow: `0 0 0 40px ${palette.BLUSH} inset`
    }
  },
  inputInputWithoutLabel: {
    padding: '22px 16px',
    transition: 'padding 0.1s',
    willChange: 'padding'
  },
  inputInputWithHelperText: {
    padding: theme.spacing(4, 2, 0.5)
  },
  inputMultiline: {
    padding: theme.spacing(4, 2, 1.5)
  },
  inputInputMultiline: {
    padding: 0
  },
  inputLabelRoot: {
    left: 4,
    color: palette.GRAPHITE,
    fontSize: '1rem',
    fontWeight: 'normal',
    '&$inputLabelFocused': {
      color: palette.INDIGO
    },
    '&.Mui-error': {
      color: palette.HOTEL_CALIFORNIA
    }
  },
  inputLabelDisabled: {
    '&.Mui-error': {
      color: alpha(theme.palette.common.black, 0.38)
    }
  },
  inputLabelFocused: {
    '&$inputLabelFilled': {
      transform: 'translate(12px, 16px) scale(0.75)'
    }
  },
  inputLabelFilled: {
    transform: 'translate(12px, 24px) scale(1)'
  },
  inputLabelShrink: {
    '&$inputLabelFilled': {
      transform: 'translate(12px, 16px) scale(0.75)'
    }
  },
  inputLabelOutlined: {
    '&$inputLabelShrink': {
      transform: 'translate(12px, 14px) scale(0.75)'
    }
  },
  formHelperTextRoot: {
    borderRadius: 'inherit',
    margin: 0,
    padding: theme.spacing(0, 2, 1),
    fontSize: 14
  },
  formerHelperTextWithAdornment: {
    padding: theme.spacing(0, 2, 1, 7)
  },
  formerHelperTextDisabled: {
    backgroundColor: palette.STONE,
    borderBottomLeftRadius: 'inherit',
    borderBottomRightRadius: 'inherit'
  },
  selectSelect: {
    overflow: 'hidden',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  selectIcon: {
    color: palette.INK,
    paddingRight: 8
  },
  menuListPadding: {
    paddingTop: 0,
    paddingBottom: 0
  },
  startAdornmentRoot: {
    padding: theme.spacing(2, 0, 2, 2),
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    minHeight: 63,
    color: palette.ROCK,
    boxSizing: 'border-box'
  },
  startAdornmentRootWithHelperText: {
    padding: theme.spacing(2, 0, 0, 2),
    minHeight: 47
  },
  startAdornmentFocused: {
    color: palette.INDIGO
  },
  startAdornmentError: {
    color: palette.HOTEL_CALIFORNIA
  },
  helperCollapse: {
    borderBottomLeftRadius: 'inherit',
    borderBottomRightRadius: 'inherit',
    '& *': {
      borderBottomLeftRadius: 'inherit',
      borderBottomRightRadius: 'inherit'
    }
  }
}));
const TextField = ({
  startAdornment,
  endAdornment,
  fullWidth = true,
  helperText,
  valid,
  variant = 'filled',
  containerProps,
  FormHelperTextProps,
  InputLabelProps,
  InputProps,
  SelectProps,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  ...props
}) => {
  const classes = useStyles();
  const [focus, setFocus] = useState(false);
  const mergedInputLabelProps = useMemo(() => _merge({
    className: classes.inputLabelRoot,
    classes: {
      focused: classes.inputLabelFocused,
      filled: classes.inputLabelFilled,
      shrink: classes.inputLabelShrink,
      outlined: classes.inputLabelOutlined,
      disabled: classes.inputLabelDisabled
    }
  }, InputLabelProps), [classes.inputLabelDisabled, classes.inputLabelFilled, classes.inputLabelFocused, classes.inputLabelOutlined, classes.inputLabelRoot, classes.inputLabelShrink, InputLabelProps]);
  const mergedInputProps = useMemo(() => _merge({
    disableUnderline: true,
    classes: {
      root: classes.inputRoot,
      focused: classes.inputFocused,
      input: clsx(classes.inputInput, {
        [classes.inputInputWithoutLabel]: !props.label,
        [classes.inputInputWithHelperText]: !!helperText,
        [classes.inputInputOutlinedError]: variant === 'outlined' && !!props.error
      }),
      multiline: classes.inputMultiline,
      inputMultiline: classes.inputInputMultiline,
      disabled: classes.inputDisabled,
      adornedEnd: clsx(classes.adornedEnd, {
        [classes.adornedEndFocused]: focus,
        [classes.adornedEndError]: props.error
      })
    },
    endAdornment: (InputProps === null || InputProps === void 0 ? void 0 : InputProps.endAdornment) || endAdornment && _jsx(InputAdornment, {
      position: "end",
      children: endAdornment
    }) || undefined
  }, InputProps), [classes.inputRoot, classes.inputFocused, classes.inputInput, classes.inputInputWithoutLabel, classes.inputInputWithHelperText, classes.inputInputOutlinedError, classes.inputMultiline, classes.inputInputMultiline, classes.inputDisabled, classes.adornedEnd, classes.adornedEndFocused, classes.adornedEndError, props.label, props.error, helperText, variant, focus, InputProps, endAdornment]);
  const mergedSelectProps = useMemo(() => _merge({
    IconComponent: ArrowDropDown,
    classes: {
      select: classes.selectSelect,
      icon: classes.selectIcon
    },
    MenuProps: {
      MenuListProps: {
        classes: {
          padding: classes.menuListPadding
        }
      }
    }
  }, SelectProps), [classes.menuListPadding, classes.selectIcon, classes.selectSelect, SelectProps]);
  const interactionProps = useMemo(() => ({
    onFocus: event => {
      setFocus(true);
      return onFocusProp === null || onFocusProp === void 0 ? void 0 : onFocusProp(event);
    },
    onBlur: event => {
      setFocus(false);
      return onBlurProp === null || onBlurProp === void 0 ? void 0 : onBlurProp(event);
    }
  }), [onBlurProp, onFocusProp]);
  const mergedContainerProps = useMemo(() => _merge({
    className: clsx(classes.container, {
      [classes.inputOutlined]: variant === 'outlined',
      [classes.inputOutlinedFocused]: variant === 'outlined' && focus,
      [classes.inputOutlinedValid]: variant === 'outlined' && valid,
      [classes.inputOutlinedValidFocused]: variant === 'outlined' && valid && focus,
      [classes.inputOutlinedError]: variant === 'outlined' && props.error,
      [classes.inputOutlinedErrorFocused]: variant === 'outlined' && props.error && focus,
      [classes.inputOutlinedDisabled]: variant === 'outlined' && props.disabled,
      [classes.inputInline]: !fullWidth
    })
  }, containerProps), [classes.container, classes.inputInline, classes.inputOutlined, classes.inputOutlinedDisabled, classes.inputOutlinedError, classes.inputOutlinedErrorFocused, classes.inputOutlinedFocused, classes.inputOutlinedValid, classes.inputOutlinedValidFocused, containerProps, focus, fullWidth, props.disabled, props.error, valid, variant]);
  const collapseProps = useMemo(() => ({
    className: classes.helperCollapse
  }), [classes.helperCollapse]);
  const inputHelperTextClasses = useMemo(() => ({
    disabled: classes.formerHelperTextDisabled
  }), [classes.formerHelperTextDisabled]);
  return _jsxs(Box, {
    ...mergedContainerProps,
    children: [_jsxs(Box, {
      className: clsx({
        [classes.disabled]: props.disabled,
        [classes.disabledWithHelperText]: props.disabled && !!helperText
      }),
      display: "flex",
      flex: 1,
      children: [startAdornment ? _jsx("div", {
        className: clsx(classes.startAdornmentRoot, {
          [classes.startAdornmentRootWithHelperText]: !!helperText,
          [classes.startAdornmentFocused]: focus,
          [classes.startAdornmentError]: !props.disabled && props.error
        }),
        children: startAdornment
      }) : null, _jsx(MuiTextField, {
        fullWidth: fullWidth,
        variant: "filled",
        InputLabelProps: mergedInputLabelProps,
        InputProps: mergedInputProps,
        SelectProps: mergedSelectProps,
        ...props,
        ...interactionProps
      })]
    }), _jsx(InputHelperText, {
      collapseProps: collapseProps,
      message: helperText,
      className: clsx(classes.formHelperTextRoot, !!startAdornment && classes.formerHelperTextWithAdornment, props.disabled && classes.formerHelperTextDisabled),
      classes: inputHelperTextClasses,
      error: props.error,
      disabled: props.disabled,
      ...FormHelperTextProps
    })]
  });
};
const MemoTextField = memo(TextField);
export default MemoTextField;